import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { type Claim, patchClaimRequest } from '../../../../models/claim'
import { ClaimInjuriesSection } from './ClaimInjuriesSection'
import { ClaimOtherVehiclesSection } from './ClaimOtherVehiclesSection'
import { ClaimPhotoExtractionBanner } from './ClaimPhotoExtractionBanner'
import { ClaimPoliceSection } from './ClaimPoliceSection'
import { claimToStep4Defaults, STEP4_DEFAULTS, STEP4_GROUPS, type Step4FormValues } from './claimStep4Values'
import { ClaimStepShell } from './ClaimStepShell'
import { mergeExtractedValues } from './mergeExtractedValues'
import { FILLED_PREFIXES, filledFromPhotos, useClaimPhotoExtraction } from './useClaimPhotoExtraction'

interface ClaimOthersInvolvedFormProps {
  claimId: string
  /** On-behalf org scope (CAI user acting for a customer) — used on the save request. */
  orgId?: string
  claim?: Claim
  onBack: () => void
  onSaved: (claim: Claim) => void
}

export function ClaimOthersInvolvedForm({
  claimId,
  orgId,
  claim,
  onBack,
  onSaved,
}: ClaimOthersInvolvedFormProps) {
  const [saving, setSaving] = useState(false)
  const [initial] = useState(() => (claim ? claimToStep4Defaults(claim) : STEP4_DEFAULTS))
  const { control, register, handleSubmit, formState, reset, getValues } = useForm<Step4FormValues>({
    defaultValues: initial,
  })
  const errors = formState.errors

  const { extracted, reading, latest } = useClaimPhotoExtraction(claim, orgId)
  useEffect(() => {
    if (!extracted) return
    reset(mergeExtractedValues(claimToStep4Defaults(extracted), getValues(), initial, STEP4_GROUPS))
  }, [extracted, reset, getValues, initial])

  async function onValid(v: Step4FormValues) {
    setSaving(true)
    try {
      const injured = v.anyInjuries === true
      const involved = v.otherVehiclesInvolved === true
      const contacted = v.policeContacted === true
      const saved = await patchClaimRequest(
        claimId,
        {
          otherVehiclesInvolved: involved,
          injuries: {
            numPassengers: Number(v.numPassengers) || 0,
            anyInjuries: v.anyInjuries,
            injuredParties: injured ? v.injuredParties : [],
            numInjured: injured && v.numInjured ? Number(v.numInjured) : null,
            transportedToHospital: injured ? v.transportedToHospital : null,
          },
          otherVehicle: involved
            ? {
                licensePlate: v.otherLicensePlate,
                otherDriverName: v.otherDriverName,
                insuranceCompany: v.otherInsuranceCompany,
                policyNumber: v.otherPolicyNumber,
                contactInfo: v.otherContactInfo,
              }
            : null,
          police: {
            policeContacted: v.policeContacted,
            policeResponded: contacted ? v.policeResponded : null,
            reportFiled: contacted ? v.reportFiled : null,
            reportCaseNumber: contacted ? v.reportCaseNumber : '',
            jurisdiction: contacted ? v.jurisdiction : '',
            citationIssued: v.citationIssued,
          },
        },
        orgId
      )
      onSaved(saved)
    } catch {
      toast.error('Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ClaimStepShell
      step={4}
      title="Others Involved"
      subtitle="Tell us about anyone who was hurt, other vehicles, and police involvement."
    >
      <ClaimPhotoExtractionBanner
        reading={reading}
        filled={filledFromPhotos(latest, FILLED_PREFIXES.others)}
      />

      <Card>
        <CardHeader>
          <CardTitle>Passengers in your vehicle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">Number of passengers *</label>
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              aria-invalid={errors.numPassengers ? true : undefined}
              {...register('numPassengers', { validate: (val) => val.trim() !== '' || 'Required.' })}
            />
            <p className="text-xs text-neutral-500">Enter 0 if you were alone</p>
            <FieldError message={errors.numPassengers?.message} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Injuries</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ClaimInjuriesSection control={control} register={register} errors={errors} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other vehicles</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ClaimOtherVehiclesSection control={control} register={register} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Police &amp; citation</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ClaimPoliceSection control={control} register={register} />
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          ← Back
        </Button>
        <Button type="button" onClick={handleSubmit(onValid)} loading={saving ? 'Saving…' : undefined}>
          Next →
        </Button>
      </div>
    </ClaimStepShell>
  )
}
